import { UserRole } from '../types';

// API Configuration - Must be set in .env file
// Example: REACT_APP_API_URL=http://localhost:5000/api
if (!process.env.REACT_APP_API_URL) {
  throw new Error(
    'REACT_APP_API_URL is not defined. Please create a .env file with REACT_APP_API_URL=http://your-api-url/api'
  );
}
export const API_BASE_URL = process.env.REACT_APP_API_URL;

// Auth Constants
export const AUTH_TOKEN_KEY = 'timesheet_auth_token';
export const AUTH_USER_KEY = 'timesheet_user';
export const AUTH_REFRESH_TOKEN_KEY = 'timesheet_refresh_token';

// App Branding
export const APP_NAME = 'OM Timesheet';
export const APP_SUBTITLE = 'ENTERPRISE';
export const APP_TAGLINE = 'Enterprise Time Management';

// Role Display Names
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Admin',
  [UserRole.PMO]: 'PMO',
  [UserRole.MANAGER]: 'Manager',
  [UserRole.EMPLOYEE]: 'Employee',
  [UserRole.IT_ADMIN]: 'IT Admin',
};

// Role Full Names
export const ROLE_FULL_NAMES: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'System Admin',
  [UserRole.PMO]: 'Project Management Officer',
  [UserRole.MANAGER]: 'Team Manager',
  [UserRole.EMPLOYEE]: 'Employee',
  [UserRole.IT_ADMIN]: 'IT Administrator',
};

// Role-based Route Paths
export const ROLE_DASHBOARD_PATHS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: '/dashboard/admin',
  [UserRole.PMO]: '/dashboard/pmo',
  [UserRole.MANAGER]: '/dashboard/manager',
  [UserRole.EMPLOYEE]: '/dashboard/employee',
  [UserRole.IT_ADMIN]: '/dashboard/it-admin',
};

// Navigation Section interface
export interface NavSection {
  title: string;
  items: NavItem[];
}

export interface NavItem {
  label: string;
  path: string;
  icon: string;
  /** Permission required to see this item (checked via hasPermission) */
  permission?: string;
  /** Alternative: any of these permissions grants access */
  anyPermission?: string[];
}

// Permission-based Navigation - items are filtered based on user permissions
export const PERMISSION_NAV_SECTIONS: NavSection[] = [
  {
    title: 'MAIN',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
      { label: 'Fill Timesheet', path: '/log-time', icon: 'clock', permission: 'timesheets.fill' },
      { label: 'My Timesheets', path: '/timesheets', icon: 'file', permission: 'timesheets.view_own' },
    ],
  },
  {
    title: 'TEAM',
    items: [
      { label: 'All Timesheets', path: '/all-timesheets', icon: 'file-text', permission: 'timesheets.view_all' },
      { label: 'My Project\'s Team', path: '/my-project-team', icon: 'users', permission: 'timesheets.view_team' },
      { label: 'My Team', path: '/team', icon: 'users', permission: 'team.view' },
      { label: 'Approvals', path: '/approvals', icon: 'check', permission: 'timesheets.approve' },
      { label: 'Work Requests', path: '/work-requests', icon: 'hand', anyPermission: ['requests.submit_work', 'requests.view_work'] },
      { label: 'All Projects', path: '/all-projects', icon: 'folder', permission: 'reports.view_projects' },
      { label: 'All Employees', path: '/all-employees', icon: 'users', permission: 'reports.view_employees' },
      { label: 'Employees on Bench', path: '/bench-employees', icon: 'users', permission: 'bench.view' },
    ],
  },
  {
    title: 'MANAGEMENT',
    items: [
      { label: 'Clients', path: '/clients', icon: 'building', permission: 'clients.view' },
      { label: 'Projects', path: '/projects', icon: 'folder', permission: 'projects.view_all' },
      { label: 'Allocations', path: '/allocations', icon: 'calendar', permission: 'projects.assign' },
      { label: 'Task Types', path: '/task-types', icon: 'list', permission: 'tasks.manage' },
    ],
  },
  {
    title: 'ORGANIZATION',
    items: [
      { label: 'Employees', path: '/employees', icon: 'users', permission: 'users.view' },
      { label: 'Departments', path: '/departments', icon: 'grid', permission: 'departments.manage' },
      { label: 'Roles', path: '/roles', icon: 'briefcase', permission: 'roles.manage' },
    ],
  },
  {
    title: 'SYSTEM',
    items: [
      { label: 'Holidays', path: '/holidays', icon: 'calendar', permission: 'holidays.manage' },
      { label: 'Settings', path: '/settings', icon: 'settings', permission: 'settings.manage' },
      { label: 'Permissions', path: '/permissions', icon: 'shield', permission: 'users.manage_permissions' },

      { label: 'Email Notifications', path: '/email-notifications', icon: 'email', permission: 'settings.manage' },

      { label: 'Data Import', path: '/data-import', icon: 'file-text', permission: 'import.manage' },

    ],
  },
  {
    title: 'ASSETS',
    items: [
      { label: 'Assets', path: '/assets', icon: 'laptop', permission: 'assets.view' },
      { label: 'Assignments', path: '/assets/assignments', icon: 'users', permission: 'assets.view' },
      { label: 'Categories', path: '/assets/categories', icon: 'folder', permission: 'asset_categories.manage' },
      { label: 'States', path: '/assets/states', icon: 'tag', permission: 'asset_states.manage' },
      { label: 'Locations', path: '/assets/locations', icon: 'map-pin', permission: 'asset_locations.manage' },
      { label: 'Asset Logs', path: '/assets/logs', icon: 'file-text', permission: 'assets.view_logs' },
    ],
  },
];

// Navigation Items for each role - organized by sections (legacy, for backwards compatibility)
export const ROLE_NAV_SECTIONS: Record<UserRole, NavSection[]> = {
  [UserRole.SUPER_ADMIN]: [
    {
      title: 'MAIN',
      items: [
        { label: 'Dashboard', path: '/dashboard/admin', icon: 'dashboard' },
        { label: 'Fill Timesheet', path: '/log-time', icon: 'clock' },
        { label: 'My Timesheets', path: '/timesheets', icon: 'file' },
        { label: 'Approvals', path: '/approvals', icon: 'check' },
      ],
    },
    {
      title: 'MANAGEMENT',
      items: [
        { label: 'Projects', path: '/projects', icon: 'folder' },
        { label: 'Clients', path: '/clients', icon: 'building' },
        { label: 'Allocations', path: '/allocations', icon: 'calendar' },
      ],
    },
    {
      title: 'ORGANIZATION',
      items: [
        { label: 'Employees', path: '/employees', icon: 'users' },
        { label: 'Departments', path: '/departments', icon: 'grid' },
        { label: 'Roles', path: '/roles', icon: 'briefcase' },
      ],
    },
  ],
  [UserRole.PMO]: [
    {
      title: 'MAIN',
      items: [
        { label: 'Dashboard', path: '/dashboard/pmo', icon: 'dashboard' },
        { label: 'Fill Timesheet', path: '/log-time', icon: 'clock' },
        { label: 'My Timesheets', path: '/timesheets', icon: 'file' },
        { label: 'All Timesheets', path: '/all-timesheets', icon: 'file-text' },
        { label: 'Approvals', path: '/approvals', icon: 'check' },
      ],
    },
    {
      title: 'MANAGEMENT',
      items: [
        { label: 'Clients', path: '/clients', icon: 'building' },
        { label: 'Projects', path: '/projects', icon: 'folder' },
        { label: 'Allocations', path: '/allocations', icon: 'calendar' },
      ],
    },
    {
      title: 'ORGANIZATION',
      items: [
        { label: 'Employees', path: '/employees', icon: 'users' },
      ],
    },
  ],
  [UserRole.MANAGER]: [
    {
      title: 'MAIN',
      items: [
        { label: 'Dashboard', path: '/dashboard/manager', icon: 'dashboard' },
        { label: 'Fill Timesheet', path: '/log-time', icon: 'clock' },
        { label: 'My Timesheets', path: '/timesheets', icon: 'file' },
      ],
    },
    {
      title: 'TEAM',
      items: [
        { label: 'My Project\'s Team', path: '/my-project-team', icon: 'users' },
        { label: 'My Team', path: '/team', icon: 'users' },
        { label: 'Approvals', path: '/approvals', icon: 'check' },
      ],
    },
  ],
  [UserRole.EMPLOYEE]: [
    {
      title: 'MAIN',
      items: [
        { label: 'Dashboard', path: '/dashboard/employee', icon: 'dashboard' },
        { label: 'Fill Timesheet', path: '/log-time', icon: 'clock' },
        { label: 'My Timesheets', path: '/timesheets', icon: 'file' },
      ],
    },
  ],
  [UserRole.IT_ADMIN]: [
    {
      title: 'MAIN',
      items: [
        { label: 'Dashboard', path: '/dashboard/it-admin', icon: 'dashboard' },
        { label: 'Fill Timesheet', path: '/log-time', icon: 'clock' },
        { label: 'My Timesheets', path: '/timesheets', icon: 'file' },
      ],
    },
    {
      title: 'ASSETS',
      items: [
        { label: 'Assets', path: '/assets', icon: 'laptop' },
        { label: 'Assignments', path: '/assets/assignments', icon: 'users' },
        { label: 'Categories', path: '/assets/categories', icon: 'folder' },
        { label: 'States', path: '/assets/states', icon: 'tag' },
        { label: 'Locations', path: '/assets/locations', icon: 'map-pin' },
        { label: 'Asset Logs', path: '/assets/logs', icon: 'file-text' },
      ],
    },
  ],
};

// Legacy flat nav items (for backwards compatibility)
export const ROLE_NAV_ITEMS: Record<UserRole, NavItem[]> = {
  [UserRole.SUPER_ADMIN]: ROLE_NAV_SECTIONS[UserRole.SUPER_ADMIN].flatMap(s => s.items),
  [UserRole.PMO]: ROLE_NAV_SECTIONS[UserRole.PMO].flatMap(s => s.items),
  [UserRole.MANAGER]: ROLE_NAV_SECTIONS[UserRole.MANAGER].flatMap(s => s.items),
  [UserRole.EMPLOYEE]: ROLE_NAV_SECTIONS[UserRole.EMPLOYEE].flatMap(s => s.items),
  [UserRole.IT_ADMIN]: ROLE_NAV_SECTIONS[UserRole.IT_ADMIN].flatMap(s => s.items),
};

// Status Colors
export const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  draft: '#6b7280',
  submitted: '#3b82f6',
  active: '#3b82f6',
  completed: '#10b981',
  on_hold: '#6b7280',
};

// Chart Colors
export const CHART_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
];

// Date Formats
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DISPLAY_DATE_FORMAT = 'MMM DD, YYYY';

// Pagination Defaults
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
